// ============================================================
// OPERATIONAL SPATIAL GRAPH — Fase 10.1 / 10.2
// Grafo de adjacências espaciais para pathfinding e análise de fluxo
// ============================================================
import type { SpatialNode, SpatialEdge, ObraZone } from '@/types/twin'

export interface SpatialGraph {
  nodes:    Map<string, SpatialNode>
  edges:    Map<string, SpatialEdge[]>   // nodeId → outgoing edges
  zones:    Map<string, ObraZone>
  zoneNodes: Map<string, string[]>       // zoneId → nodeIds
}

// ─── Build graph ──────────────────────────────────────────────
export function buildSpatialGraph(
  nodes: SpatialNode[],
  edges: SpatialEdge[],
  zones: ObraZone[],
): SpatialGraph {
  const graph: SpatialGraph = {
    nodes:     new Map(nodes.map(n => [n.id, n])),
    edges:     new Map(),
    zones:     new Map(zones.map(z => [z.id, z])),
    zoneNodes: new Map(),
  }

  // Build adjacency list
  for (const edge of edges) {
    if (!graph.edges.has(edge.from_node_id)) {
      graph.edges.set(edge.from_node_id, [])
    }
    graph.edges.get(edge.from_node_id)!.push(edge)
  }

  // Map zones to nodes
  for (const node of nodes) {
    if (node.zone_id) {
      if (!graph.zoneNodes.has(node.zone_id)) graph.zoneNodes.set(node.zone_id, [])
      graph.zoneNodes.get(node.zone_id)!.push(node.id)
    }
  }

  return graph
}

// ─── Dijkstra pathfinding ─────────────────────────────────────
export function findPath(
  graph:   SpatialGraph,
  fromId:  string,
  toId:    string,
): string[] | null {
  if (!graph.nodes.has(fromId) || !graph.nodes.has(toId)) return null

  const dist: Map<string, number> = new Map()
  const prev: Map<string, string | null> = new Map()
  const visited = new Set<string>()
  const queue = new Set<string>()

  for (const id of graph.nodes.keys()) {
    dist.set(id, Infinity)
    prev.set(id, null)
    queue.add(id)
  }
  dist.set(fromId, 0)

  while (queue.size > 0) {
    // Find min dist node in queue
    let u: string | null = null
    let minD = Infinity
    for (const id of queue) {
      const d = dist.get(id) ?? Infinity
      if (d < minD) { minD = d; u = id }
    }
    if (!u || u === toId) break
    queue.delete(u)
    visited.add(u)

    const edges = graph.edges.get(u) ?? []
    for (const edge of edges) {
      if (edge.is_blocked) continue
      const alt = (dist.get(u) ?? Infinity) + edge.weight
      if (alt < (dist.get(edge.to_node_id) ?? Infinity)) {
        dist.set(edge.to_node_id, alt)
        prev.set(edge.to_node_id, u)
      }
    }
  }

  // Reconstruct path
  const path: string[] = []
  let current: string | null = toId
  while (current) {
    path.unshift(current)
    current = prev.get(current) ?? null
  }

  return path.length > 0 && path[0] === fromId ? path : null
}

// ─── Zone neighbors ───────────────────────────────────────────
export function getZoneNeighbors(graph: SpatialGraph, zoneId: string): string[] {
  const nodeIds = graph.zoneNodes.get(zoneId) ?? []
  const neighborZones = new Set<string>()

  for (const nodeId of nodeIds) {
    const edges = graph.edges.get(nodeId) ?? []
    for (const edge of edges) {
      const target = graph.nodes.get(edge.to_node_id)
      if (target?.zone_id && target.zone_id !== zoneId) {
        neighborZones.add(target.zone_id)
      }
    }
  }
  return [...neighborZones]
}

// ─── Topological hierarchy ────────────────────────────────────
export function buildZoneHierarchy(
  zones: ObraZone[],
): Map<string, ObraZone[]> {  // parentId → children
  const hierarchy = new Map<string, ObraZone[]>()

  for (const zone of zones) {
    const parent = zone.parent_id ?? '__root__'
    if (!hierarchy.has(parent)) hierarchy.set(parent, [])
    hierarchy.get(parent)!.push(zone)
  }
  return hierarchy
}

// ─── Aggregate zone risk ──────────────────────────────────────
export function aggregateZoneRisk(
  zone:    ObraZone,
  children: ObraZone[],
): 'low' | 'moderate' | 'high' | 'critical' {
  const all = [zone, ...children]
  const levels: Record<string, number> = { low: 0, moderate: 1, high: 2, critical: 3 }
  let max = 0
  for (const z of all) {
    const lvl = levels[z.risk_level] ?? 0
    if (lvl > max) max = lvl
  }
  return (['low', 'moderate', 'high', 'critical'][max] ?? 'low') as ObraZone['risk_level']
}

// ─── Distance between two nodes (Euclidean on normalized grid) ─
export function nodeDistance(a: SpatialNode, b: SpatialNode): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

// ─── Blocked routes detection ─────────────────────────────────
export function detectBlockedRoutes(graph: SpatialGraph): SpatialEdge[] {
  const blocked: SpatialEdge[] = []
  for (const edges of graph.edges.values()) {
    for (const edge of edges) {
      if (edge.is_blocked) blocked.push(edge)
    }
  }
  return blocked
}
